// `/pure`, NOT the default entry. Importing "@stripe/stripe-js" injects the
// js.stripe.com script as a SIDE EFFECT OF THE IMPORT — before loadStripe() is
// ever called — so deferring the call alone changes nothing. Verified on dev:
// after moving the calls into this getter, Stripe still loaded on /, /music,
// /pricing and /terms-of-use and still set all three cookies. The `/pure`
// entry point exists precisely for on-demand loading and injects nothing until
// loadStripe() runs. Do not "simplify" this back to the default import.
import type { Stripe } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";

/**
 * Lazy Stripe.js loader.
 *
 * Two separate things had to be fixed, and only the second one is obvious from
 * reading the code.
 *
 * `loadStripe()` used to be called at MODULE level in four files
 * (PaymentView, SubPaymentView, UpgradePaymentView, UpdatePaymentMethodModal).
 * Module-level means it ran as soon as any of those modules was imported —
 * which, because the cart modal is mounted by the layout, was on every page
 * load. Stripe.js then set its fraud cookies (`__stripe_mid`, `__stripe_sid`,
 * plus `m` on m.stripe.com) site-wide, for visitors who never went near a
 * checkout.
 *
 * That forced the Cookie Policy to defend those cookies as "necessary" under
 * TKG 2021 §165(3) on a page where nothing was being paid for. Loading Stripe
 * only when a payment surface actually renders makes the "necessary for
 * payment" basis exact instead of arguable — a code answer to what would
 * otherwise be a legal question.
 *
 * Call it from the render path that needs it (`<Elements stripe={getStripe()}>`).
 * The promise is memoised, so repeated calls across the four surfaces reuse one
 * script load and the reference stays stable across re-renders — which
 * `<Elements>` requires.
 */
let stripePromise: Promise<Stripe | null> | null = null;

export const getStripe = (): Promise<Stripe | null> => {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");
  }
  return stripePromise;
};
